"use client"

import { Button } from "@/components/ui/button"
import Link from "next/link"
import { useEffect, useState } from "react"
import AnimatedCard from "../ui/animatedCard/animatedCard"
import { useTranslation } from "react-i18next"

type Category = {
  id: number
  name: string
  description: string
  handle: string
  mpath: string
  isActive: number
  isInternal: number
  category_rank: number
  parent_category_id: number | null
  logo_image: string
  background_image: string
  character_image: string
  created_at: string
  updated_at: string
  deleted_at: string | null
}

type Props = {}

const S3_IMAGE_BASE_URL = process.env.NEXT_PUBLIC_S3_IMAGE_BASE_URL
const BACKEND_URL = process.env.NEXT_PUBLIC_MEDUSA_BACKEND_URL

const PopularSection = (props: Props) => {
  const { t } = useTranslation()
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    const abortController = new AbortController()
    let isMounted = true

    const fetchPopularCategories = async () => {
      try {
        setLoading(true)
        setError(false)

        const response = await fetch(`${BACKEND_URL}/store/popular-categories`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          signal: abortController.signal,
        })

        if (!isMounted) {
          return
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch popular categories: ${response.statusText}`)
        }

        const data = await response.json()

        if (!isMounted) {
          return
        }

        if (data?.data && Array.isArray(data.data)) {
          setCategories(data.data)
        } else {
          setCategories([])
        }
      } catch (err: any) {
        // Ignore abort errors - they're expected in React Strict Mode
        if (err.name === 'AbortError') {
          return
        }
        if (isMounted) {
          setError(true)
          setCategories([])
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchPopularCategories()

    return () => {
      isMounted = false
      abortController.abort()
    }
  }, [])

  if (loading) {
    return (
      <div className="max-w-[1437px] mx-auto px-6 mt-8">
        <div className="mb-8 flex items-center rounded-xl border border-orange-200 bg-accent/60 px-4 py-3">
          <div className="text-2xl md:min-w-max text-ui-fg-subtle">
            {t("home.popularGames")}
          </div>
          <div className="mx-8 h-px flex-1 bg-button-primary" />
          <Link href="/categories">
            <Button className="w-fit border border-orange-300 bg-white text-lg text-button-primary transition-all duration-200 ease-in-out hover:scale-110 hover:bg-button-primary hover:text-white">
              {t("home.allCategories")}
            </Button>
          </Link>
        </div>
        <div className="flex justify-center w-full">
          <div className="flex overflow-x-auto gap-6 pb-4 no-scrollbar">
            {[1, 2, 3, 4].map((index) => (
              <div key={index} className="flex items-stretch shrink-0 w-[300px] h-[400px] bg-bg-component animate-pulse rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error || categories.length === 0) {
    return null
  }

  return (
    <div className="max-w-[1437px] mx-auto px-6 mt-8">
      <div className="mb-8 flex items-center rounded-xl border border-orange-200 bg-accent/60 px-4 py-3">
        <div className="text-2xl md:min-w-max text-ui-fg-subtle">
          {t("home.popularGames")}
        </div>
        <div className="mx-8 h-px flex-1 bg-button-primary" />
        <Link href="/categories">
          <Button className="w-fit border border-orange-300 bg-white text-lg text-button-primary transition-all duration-200 ease-in-out hover:scale-110 hover:bg-button-primary hover:text-white">
            {t("home.allCategories")}
          </Button>
        </Link>
      </div>

      <div className="flex justify-center w-full">
        <div className="flex overflow-x-auto gap-6 pb-4 no-scrollbar">
          {categories.map((category, index) => (
            <div key={category.id || index} className="flex items-stretch shrink-0">
              <AnimatedCard
                bgImage={`${S3_IMAGE_BASE_URL}${category.background_image}`}
                titleImage={category.logo_image ? `${S3_IMAGE_BASE_URL}${category.logo_image}` : undefined}
                characterImage={category.character_image ? `${S3_IMAGE_BASE_URL}${category.character_image}` : undefined}
                redirectUrl={`/categories/${category.handle}`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default PopularSection
